import React from "react";
import { storyblokEditable, useStoryblokState } from "@storyblok/react";

type TableColumn = {
  _uid: string;
  column_title: string;
  column_key: string;
};

type TableCell = {
  _uid: string;
  column_key: string;
  value: string;
};

type TableRow = {
  _uid: string;
  cells?: TableCell[];
};

type ReusableTableProps = {
  blok: {
    _uid: string;
    _editable?: string;
    table_columns?: TableColumn[];
    table_rows?: TableRow[];
  };
};

const ReusableTable: React.FC<ReusableTableProps> = ({ blok }) => {
  const liveBlok = useStoryblokState(blok);
  const columns: TableColumn[] = liveBlok?.table_columns || [];
  const rows: TableRow[] = liveBlok?.table_rows || [];

  if (!columns.length || !rows.length) {
    console.error("Missing table data. Check your Storyblok setup.");
    return (
      <div className="p-4 bg-red-50 text-red-600 border border-red-200 rounded">
        Table configuration incomplete. Please check:
        <ul className="list-disc pl-5 mt-2">
          <li>Are columns defined?</li>
          <li>Are rows added?</li>
          <li>Do cell column_keys match column keys?</li>
        </ul>
        <pre className="mt-2 text-xs">{JSON.stringify(blok, null, 2)}</pre>
      </div>
    );
  }

  return (
    <section {...storyblokEditable(liveBlok)} className="py-10">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full overflow-x-auto">
          <div className="inline-block min-w-full">
            <table className="w-full min-w-[650px] text-left border rounded-xl bg-white table-fixed">
              <thead>
                <tr>
                  {columns.map((col: TableColumn) => (
                    <th
                      key={col._uid}
                      className="bg-[#f5f5f5] px-5 py-3 font-semibold text-sm text-gray-700 whitespace-nowrap"
                    >
                      {col.column_title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row: TableRow) => (
                  <tr key={row._uid} className="border-t">
                    {columns.map((col: TableColumn) => {
                      const cell: TableCell | undefined = row.cells?.find(
                        (c: TableCell) => c.column_key === col.column_key
                      );

                      return (
                        <td
                          key={`${row._uid}-${col._uid}`}
                          className={`px-5 py-4 text-sm text-gray-800 whitespace-pre-wrap ${
                            col.column_key === "feature"
                              ? "bg-[#e7f0ff] font-medium text-[#0e3e84]"
                              : ""
                          }`}
                          data-column={col.column_key}
                        >
                          {cell?.value || "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReusableTable;
